import React, { useState, useEffect } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  ActionButton,
  B2BSpinner,
  B2BTextField,
  Icon,
  IconName,
  emailRegex,
} from "halifax";
import { useDispatch } from "react-redux";
import {
  CONFIRM_RESEND_EMAIL,
  VIEWED_RESEND_EMAIL,
  BookingType,
  CallState,
  Car,
  Flight,
  Hotel,
  ItineraryEnum,
  ItineraryWithType,
  ResendEmailProperties,
  Home,
  Experience,
  RESEND_EMAIL_SUBMIT,
  ResendEmailSubmitProperties,
  EmailBookingId,
  Uuid,
} from "redmond";

import { trackEvent } from "../../../../../../../../api/v1/analytics/trackEvent";
import { setOpenModal } from "../../../../../../actions/actions";
import * as constants from "./constants";
import "./styles.scss";
import resendConfirmationEmail from "../../../../../../../../api/v1/itinerary/resendConfirmationEmail";
import { useIsTravelApp } from "@capone/common";

interface IResendConfirmationModalContentProps {
  itinerary: ItineraryWithType;
  isActivityReservation?: Boolean;
}

const ResendConfirmationModalContent = (
  props: IResendConfirmationModalContentProps,
) => {
  const { itinerary, isActivityReservation } = props;
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [reqState, setReqState] = useState(CallState.NotCalled);

  const { isTravelApp } = useIsTravelApp();

  /**
   * @description Updates the redux store to close this modal
   */
  const closeModal = () => {
    const closedModal = { type: null, selectedItinerary: null };

    dispatch(setOpenModal(closedModal));
  };

  useEffect(() => {
    const properties: ResendEmailProperties = {
      screen: itinerary.type,
    };

    if (itinerary.type === ItineraryEnum.Hotel) {
      const hotelRes = itinerary as Hotel;
      properties.reservation_booking_id = hotelRes.reservation.reservationBookingId;
    }

    trackEvent({
      eventName: VIEWED_RESEND_EMAIL,
      properties,
    });
  }, []);

  const getEmailType = () => {
    const { type } = itinerary;

    switch (type) {
      case ItineraryEnum.Flight:
        const flightRes = itinerary as Flight;
        return {
          type: BookingType.Flight,
          value: flightRes.bookedItinerary.id as Uuid,
        };
      case ItineraryEnum.Hotel:
        const hotelRes = itinerary as Hotel;
        return {
          type: BookingType.Lodging,
          value: {
            value: hotelRes.reservation.reservationBookingId as Uuid,
          } as EmailBookingId,
        };
      case ItineraryEnum.Car:
        const carRes = itinerary as Car;
        return {
          type: BookingType.Ground,
          value: {
            value: carRes.bookResult.groundBookingId as Uuid,
          } as EmailBookingId,
        };
      case ItineraryEnum.Watch:
        return {
          type: null,
          value: null,
        };
      case ItineraryEnum.Home:
        const homeRes = itinerary as Home;
        return {
          type: BookingType.Homes,
          value: {
            value: homeRes.reservation.cartOrderedProductId as Uuid,
          } as EmailBookingId,
        };
      case ItineraryEnum.Experience:
        const reservation = itinerary as Experience;
        return {
          type: isActivityReservation
            ? BookingType.Activities
            : BookingType.Experiences,
          value: {
            value: reservation.reservation.bookingSession
              ?.cartOrderedProductId as Uuid,
          } as EmailBookingId,
        };
      default:
        return {};
    }
  };

  /**
   * @description Forward request to BE to handle sending the email
   */
  const resendConfirmation = () => {
    const product = getEmailType();

    if (product.type) {
      setReqState(CallState.InProcess);

      const confirmProperties: ResendEmailProperties = {
        screen: itinerary.type,
      };

      if (itinerary.type === ItineraryEnum.Hotel) {
        const hotelRes = itinerary as Hotel;
        confirmProperties.reservation_booking_id = hotelRes.reservation.reservationBookingId;
      }

      trackEvent({
        eventName: CONFIRM_RESEND_EMAIL,
        properties: confirmProperties,
      });

      resendConfirmationEmail({
        emailAddress: email,
        product: product,
      })
        .then(() => {
          setReqState(CallState.Success);

          const successProperties: ResendEmailSubmitProperties = {
            screen: itinerary.type,
            success: true,
          };

          if (itinerary.type === ItineraryEnum.Hotel) {
            const hotelRes = itinerary as Hotel;
            successProperties.reservation_booking_id = hotelRes.reservation.reservationBookingId;
          }

          trackEvent({
            eventName: RESEND_EMAIL_SUBMIT,
            properties: successProperties,
          });
        })
        .catch(() => {
          setReqState(CallState.Failed);

          const failureProperties: ResendEmailSubmitProperties = {
            screen: itinerary.type,
            success: false,
          };

          if (itinerary.type === ItineraryEnum.Hotel) {
            const hotelRes = itinerary as Hotel;
            failureProperties.reservation_booking_id = hotelRes.reservation.reservationBookingId;
          }

          trackEvent({
            eventName: RESEND_EMAIL_SUBMIT,
            properties: failureProperties,
          });
        });
    }
  };

  /**
   * @description Gets the popup actions based on the state of the resend request
   * @return {JSX.Element}
   */
  const getResendActions = () => {
    const CANCEL_BTN = (
      <ActionButton
        defaultStyle="h4r-secondary"
        message={constants.CANCEL}
        onClick={closeModal}
      />
    );

    switch (reqState) {
      case CallState.Failed:
        return (
          <>
            <ActionButton
              className="try-again-btn"
              defaultStyle="h4r-primary"
              message={constants.TRY_AGAIN}
              onClick={() => setReqState(CallState.NotCalled)}
            />
            {CANCEL_BTN}
          </>
        );
      case CallState.InProcess:
        return <B2BSpinner className="loading-spinner" />;
      case CallState.NotCalled:
        return (
          <>
            <ActionButton
              className="resend-confirmation-btn"
              defaultStyle="h4r-primary"
              disabled={!emailRegex.test(email)}
              message={constants.RESEND}
              onClick={resendConfirmation}
            />
            {CANCEL_BTN}
          </>
        );
      case CallState.Success:
        return isTravelApp ? undefined : (
          <ActionButton
            className="done-btn"
            defaultStyle="h4r-primary"
            message={constants.DONE}
            onClick={closeModal}
          />
        );
      default:
        return null;
    }
  };

  /**
   * @description Gets the secondary text in the popup
   * @return {String}
   */
  const getSubtitleText = () => {
    switch (reqState) {
      case CallState.Failed:
        return constants.FAILED;
      case CallState.Success:
        return constants.SUCCEEDED;
      default:
        return constants.INSTRUCTIONS;
    }
  };

  return (
    <Box className="resend-confirmation-modal-content">
      <Box className="icon-container">
        {reqState === CallState.Failed && (
          <Icon className="error-icon" name={IconName.ErrorState} />
        )}
        {reqState === CallState.Success && (
          <Icon className="success-icon" name={IconName.Checked} />
        )}
      </Box>
      <Typography className="title" variant="h2">
        {constants.TITLE}
      </Typography>
      <Typography className="subtitle">{getSubtitleText()}</Typography>
      {(reqState === CallState.InProcess ||
        reqState === CallState.NotCalled) && (
        <Box className="resend-confirmation-form">
          <B2BTextField
            className="email-input"
            label={constants.EMAIL_LABEL}
            onChange={setEmail}
            type="email"
            value={email}
          />
        </Box>
      )}
      <Box className="actions-container">{getResendActions()}</Box>
    </Box>
  );
};

export default ResendConfirmationModalContent;
